export type StableFeatureFlag =
    'applets' |
    'attributes' |
    'serialNumbers' |
    'processes' |
    'schedules' |
    'performanceConstraintAnalysisReport' |
    'performanceOeeReport' |
    'performanceFaultReport' |
    'performanceMtbfMttrReport' |
    'performanceStateReport' |
    'editMaterialNoteComment' |
    'api';

/**
 * we want unstable feature flags to eventually become stable, or become unnecessary
 */
export type UnstableFeatureFlag =
    'unstableWorkInstructions' | // not stable until we can handle multiple options for WIs, as mentioned here https://gitlab.com/redviking/argonaut/v4/core/-/merge_requests/862#note_2713990787
    'unstableMaterialMarriage' | // not stable until we protect against cicular marriages and add root assembly ids to marriages
    'unstableMaterialInstanceMedia' | // unstable until we figure out collection media
    'unstableMaterialTransition' | // unstable while this gets fleshed out with the borg more
    'unstableWorkOrders' |
    'unstableActivationWizard' |
    'unstableActiveOperator' |
    'unstableFunctionFlows' |
    'unstablePrivilegedFlows' |
    'unstableConnections' |
    'unstableRelaxedValidationForLegacyApplets'; // temporarily needed at mesa until they get connections runtime and typed sparkplug metricExpressions with a dataType

// this list must be kept in sync with the helm chart value options
export type FeatureFlag = StableFeatureFlag | UnstableFeatureFlag;

/**
 * "soft check" to help devs remember to update the helm chart when feature flags are modified.
 */
const helmCheck: {
    [flag in FeatureFlag]: 'This feature flag has a corresponding helm chart option';
} = {
    editMaterialNoteComment: 'This feature flag has a corresponding helm chart option',
    applets: 'This feature flag has a corresponding helm chart option',
    attributes: 'This feature flag has a corresponding helm chart option',
    serialNumbers: 'This feature flag has a corresponding helm chart option',
    processes: 'This feature flag has a corresponding helm chart option',
    schedules: 'This feature flag has a corresponding helm chart option',
    api: 'This feature flag has a corresponding helm chart option',
    unstableMaterialMarriage: 'This feature flag has a corresponding helm chart option',
    unstableMaterialInstanceMedia: 'This feature flag has a corresponding helm chart option',
    unstableMaterialTransition: 'This feature flag has a corresponding helm chart option',
    unstableWorkInstructions: 'This feature flag has a corresponding helm chart option',
    unstableWorkOrders: 'This feature flag has a corresponding helm chart option',
    unstableActivationWizard: 'This feature flag has a corresponding helm chart option',
    unstableActiveOperator: 'This feature flag has a corresponding helm chart option',
    unstablePrivilegedFlows: 'This feature flag has a corresponding helm chart option',

    // performance related feature flags should follow the same naming convention
    performanceConstraintAnalysisReport: 'This feature flag has a corresponding helm chart option',
    performanceOeeReport: 'This feature flag has a corresponding helm chart option',
    performanceFaultReport: 'This feature flag has a corresponding helm chart option',
    performanceMtbfMttrReport: 'This feature flag has a corresponding helm chart option',
    performanceStateReport: 'This feature flag has a corresponding helm chart option',

    unstableFunctionFlows: 'This feature flag has a corresponding helm chart option',
    unstableConnections: 'This feature flag has a corresponding helm chart option',

    // legacy pain
    unstableRelaxedValidationForLegacyApplets: 'This feature flag has a corresponding helm chart option',
};

export const featureFlags = Object.keys(helmCheck) as [ FeatureFlag, ...FeatureFlag[] ]; // using tuple notation to work with zod enums
export const performanceFeatureFlags = featureFlags.filter(flag => flag.startsWith('performance'));

/**
 * feature flags to enable in development
 */
export const devFeatureFlags: {
    [flag in FeatureFlag]: boolean;
} = {
    applets: true,
    attributes: true,
    serialNumbers: true,
    processes: true,
    schedules: true,
    performanceConstraintAnalysisReport: true,
    performanceOeeReport: true,
    performanceFaultReport: true,
    performanceMtbfMttrReport: true,
    performanceStateReport: true,
    api: true,
    editMaterialNoteComment: true,
    unstableMaterialMarriage: true,
    unstableMaterialInstanceMedia: true,
    unstableMaterialTransition: true,
    unstableWorkInstructions: true,
    unstableWorkOrders: true,
    unstableActivationWizard: true,
    unstableActiveOperator: true,
    unstableFunctionFlows: true,
    unstableConnections: true,
    unstablePrivilegedFlows: true,
    unstableRelaxedValidationForLegacyApplets: false,
};

export const defaultFeatureFlags: {
    [flag in FeatureFlag]: false;
} = {
    editMaterialNoteComment: false,
    applets: false,
    attributes: false,
    serialNumbers: false,
    processes: false,
    schedules: false,
    performanceConstraintAnalysisReport: false,
    performanceOeeReport: false,
    performanceFaultReport: false,
    performanceMtbfMttrReport: false,
    performanceStateReport: false,
    api: false,
    unstableMaterialMarriage: false,
    unstableMaterialTransition: false,
    unstableMaterialInstanceMedia: false,
    unstableWorkInstructions: false,
    unstableWorkOrders: false,
    unstableActivationWizard: false,
    unstableActiveOperator: false,
    unstableFunctionFlows: false,
    unstableConnections: false,
    unstablePrivilegedFlows: false,
    unstableRelaxedValidationForLegacyApplets: false,
};

export type FeatureFlags = Record<FeatureFlag, boolean>;

/**
 * @note it's important that this is not a function, so the logic is not arbitrary.
 */
export type FeatureFlagRequirement = FeatureFlag | {
    and: FeatureFlagRequirement[];
} | {
    or: FeatureFlagRequirement[];
}

/**
 * Tests whether the given feature flag requirements are satisfied by the provided feature flags.
 *
 * `requirements` are AND'd together
 */
export function testFeatureFlagRequirements (requirements: FeatureFlagRequirement[], featureFlags: Partial<FeatureFlags>): boolean {
    const completeFeatureFlags: FeatureFlags = {
        ...defaultFeatureFlags,
        ...featureFlags,
    };
    if (requirements.length === 0) {
        return true; // no requirements means all are satisfied
    }

    for (const req of requirements) {
            if (typeof req === 'string') {
                if (req in completeFeatureFlags && !completeFeatureFlags[req]) {
                    return false;
                }
            } else if (typeof req === 'object' && 'or' in req) {
            // OR group of requirements (can be nested)
            if (!req.or.some(subReq => testFeatureFlagRequirements([ subReq ], completeFeatureFlags))) {
                return false;
            }
        } else if (typeof req === 'object' && 'and' in req) {
            // AND group of requirements (can be nested)
            if (!req.and.every(subReq => testFeatureFlagRequirements([ subReq ], completeFeatureFlags))) {
                return false;
            }
        }
    }
    return true; // All requirements are satisfied
}
